/**
 * Menu screens (loading and start screen): game name, full-screen background images, footer and the
 * 3D rendering while an opaque menu hides the scene. Image files live in public/assets/ui/ (see the
 * README there) – the user's final artwork simply overwrites the placeholder files.
 */

/** One background image with the point that stays visible when `object-fit: cover` crops it. */
export interface BackdropImage {
  readonly url: string;
  /** CSS object-position, e.g. '50% 60%' (keep the road / the figures' feet in view). */
  readonly focalPoint: string;
}

/**
 * While the opaque loading / start screen covers the canvas the scene is still rendered now and then:
 * every frame for `warmupSeconds` after it was covered or changed (shader compilation, a switched
 * character), then one frame every `frameInterval` seconds – saves battery on phones.
 */
/** A link in the menu footer (opens in a new tab so a running session is not lost). */
export interface LegalLink {
  readonly label: string;
  readonly url: string;
}

export interface CoveredRenderingConfig {
  readonly warmupSeconds: number;
  readonly frameInterval: number;
}

export interface UiConfig {
  /** Game logo of the menus (transparent PNG); width / height reserve its space before it has loaded. */
  readonly logo: { readonly url: string; readonly alt: string; readonly width: number; readonly height: number };
  readonly tagline: string;
  /** Full-screen background of the loading and start screen (hides the 3D scene completely). */
  readonly backdrop: {
    /** 16:9 (user art 1672 x 941) – desktop and landscape phones. */
    readonly landscape: BackdropImage;
    /** Portrait (user art 3:4, 1086 x 1448; phones crop the sides) – phones in portrait. */
    readonly portrait: BackdropImage;
    /** Media query that picks the portrait image (also switches the menus to the stacked mobile layout in CSS). */
    readonly portraitMedia: string;
  };
  readonly footer: {
    /** Official TR logo (copy of design/00_master/ci/ructec_logo.svg), shown on a white round badge. */
    readonly logoUrl: string;
    readonly website: string;
    readonly websiteUrl: string;
    readonly claim: string;
    /** Imprint and privacy policy – required on the public domain; both live on ructec.com (one source). */
    readonly legalLinks: readonly LegalLink[];
  };
  /** Sign-up and login run on ructec.com (one account for the portal and the game). */
  readonly account: { readonly registerUrl: string; readonly loginUrl: string };
  /**
   * Leaderboard pages: rows of the full list and of the start-screen preview. Until the server-side leaderboard
   * exists the rows stay empty ("Platz frei") – never filled with made-up names.
   */
  readonly leaderboard: { readonly listSize: number; readonly previewSize: number };
  readonly coveredRendering: CoveredRenderingConfig;
}

export const UI_CONFIG: UiConfig = {
  logo: { url: '/assets/ui/bauralley_logo.png', alt: 'BAU RALLEY – powered by ructec.com', width: 1200, height: 421 },
  tagline: 'Lauf. Sammle. Überlebe.',
  backdrop: {
    // the user's start scenes: both runners on the right half (landscape) / in the middle (portrait)
    landscape: { url: '/assets/ui/start_bg_desktop.jpg', focalPoint: '62% 45%' },
    portrait: { url: '/assets/ui/start_bg_mobile.jpg', focalPoint: '58% 40%' },
    portraitMedia: '(orientation: portrait)',
  },
  footer: {
    logoUrl: '/assets/ui/ructec_logo.svg',
    website: 'ructec.com',
    websiteUrl: 'https://ructec.com',
    claim: 'Vom Bau. Für den Bau.',
    legalLinks: [
      { label: 'Impressum', url: 'https://ructec.com/impressum' },
      { label: 'Datenschutz', url: 'https://ructec.com/datenschutz#bauralley' },
    ],
  },
  account: { registerUrl: 'https://ructec.com/registrieren', loginUrl: 'https://ructec.com/login' },
  leaderboard: { listSize: 10, previewSize: 3 },
  coveredRendering: { warmupSeconds: 1.5, frameInterval: 0.5 },
};
